import React, { useState, useEffect } from 'react';
import { RefreshCcw, ChevronDown, Filter } from 'lucide-react';
import { FilterState } from './types';
import MultiSelectDropdownInline from '../multi-select-inline';

interface MonthlyOrganizerActivityFiltersProps {
  activeFilters: FilterState;
  setActiveFilters: React.Dispatch<React.SetStateAction<FilterState>>;
  provinces: { id: string; name: string }[];
  localUnions: { id: string; name: string }[];
  unionStatusOptions: { id: string; name: string }[];
  organizers: { id: string; name: string; role: { id: string; roleName: string } }[];
  monthOptions: { id: string; name: string }[];
  yearOptions: { id: string; name: string }[];
  handleApplyFilters: () => void;
  handleFilterClearAll: () => void;
  isMonthlyOrganizerActivityFiltersValid: () => boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  userRole?: string | null;
}

const MonthlyOrganizerActivityFilters: React.FC<MonthlyOrganizerActivityFiltersProps> = ({
  activeFilters,
  setActiveFilters,
  provinces,
  localUnions,
  unionStatusOptions,
  organizers,
  monthOptions,
  yearOptions,
  handleApplyFilters,
  handleFilterClearAll,
  isMonthlyOrganizerActivityFiltersValid,
  setLoading,
  userRole
}) => {
  // Check if any filters are applied
  const hasActiveFilters = () => {
    return (
      (activeFilters.month && activeFilters.month.length > 0) ||
      (activeFilters.year && activeFilters.year.length > 0) ||
      (activeFilters.organizer && activeFilters.organizer.length > 0) ||
      (activeFilters.province && activeFilters.province.length > 0) ||
      (activeFilters.unionStatus && activeFilters.unionStatus.length > 0) ||
      (activeFilters.localUnion && activeFilters.localUnion.length > 0)
    );
  };

  // State for collapsible filter section - expand if filters are applied
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(hasActiveFilters());

  // Sync expanded state when active filters change
  useEffect(() => {
    // Auto-expand when filters are applied, collapse when cleared
    setIsFiltersExpanded(hasActiveFilters());
  }, [activeFilters]);

  return (
    <div className="bg-white rounded-[12px] shadow mb-4">
      {/* Filter Toggle Button */}
      <div className="p-3 sm:p-4 border-b border-gray-200">
        <button
          onClick={() => setIsFiltersExpanded(!isFiltersExpanded)}
          className="flex items-center gap-2 text-gray-700 hover:text-gray-900"
          id="toggle-monthly-filters-button"
        >
          <Filter className="w-5 h-5" />
          <span className="font-medium">Filters</span>
          <ChevronDown
            className={`w-4 h-4 transition-transform duration-200 ${isFiltersExpanded ? 'rotate-180' : ''}`}
          />
        </button>
      </div>

      {/* Collapsible Filter Content */}
      {isFiltersExpanded && (
        <div className="p-3 sm:p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 items-end">
        {/* Month Selection */}
        <div className="w-full">
          <label className="block text-sm text-gray-600 mb-1">
            Month <span className="text-red-500">*</span>
          </label>
          <div id="month-dropdown-container" className="relative">
            <MultiSelectDropdownInline
              testId="month-dropdown"
              options={monthOptions}
              onSelect={(selectedIds: string[]) => {
                setActiveFilters(prev => ({
                  ...prev,
                  month: selectedIds
                }));
              }}
              getValue={(option: { id: string; name: string }) => option.id}
              getDisplayText={(option: { id: string; name: string }) => option.name}
              selectedValues={activeFilters.month || []}
              placeholder="Select month"
              dropdownContainerClassName="dropdown-append"
            />
          </div>
        </div>

        {/* Year Selection */}
        <div className="w-full">
          <label className="block text-sm text-gray-600 mb-1">
            Year <span className="text-red-500">*</span>
          </label>
          <div id="year-dropdown-container" className="relative">
            <MultiSelectDropdownInline
              testId="year-dropdown"
              options={yearOptions}
              onSelect={(selectedIds: string[]) => {
                setActiveFilters(prev => ({
                  ...prev,
                  year: selectedIds
                }));
              }}
              getValue={(option: { id: string; name: string }) => option.id}
              getDisplayText={(option: { id: string; name: string }) => option.name}
              selectedValues={activeFilters.year || []}
              placeholder="Select year"
              dropdownContainerClassName="dropdown-append"
            />
          </div>
        </div>

        {/* Type Selection */}
        <div className="w-full">
          <label className="block text-sm text-gray-600 mb-1">Type</label>
          <div id="type-dropdown-container" className="relative">
            <MultiSelectDropdownInline
              testId="type-dropdown"
              options={[
                { id: 'Jobsite', name: 'Jobsite' },
                { id: 'Jobsite Visit', name: 'Jobsite Visit' }
              ]}
              onSelect={(selectedIds: string[]) => {
                setActiveFilters(prev => ({
                  ...prev,
                  type: selectedIds
                }));
              }}
              getValue={(option: { id: string; name: string }) => option.id}
              getDisplayText={(option: { id: string; name: string }) => option.name}
              selectedValues={activeFilters.type || []}
              placeholder="Select type"
              dropdownContainerClassName="dropdown-append"
            />
          </div>
        </div>

        {/* Organizer Section - Hidden for organizer role */}
        {userRole !== 'organizer' && (
          <div className="w-full">
            <label className="block text-sm text-gray-600 mb-1">Organizer</label>
            <div id="monthly-organizer-dropdown-container" className="relative">
              <MultiSelectDropdownInline
                testId="monthly-organizer-dropdown"
                options={organizers.filter(organizer => organizer.role.roleName !== "interRepre")}
                onSelect={(selectedIds: string[]) => {
                  setActiveFilters(prev => ({
                    ...prev,
                    organizer: selectedIds
                  }));
                }}
                getValue={(option: { id: string; name: string }) => option.id}
                getDisplayText={(option: { id: string; name: string }) => option.name}
                selectedValues={activeFilters.organizer || []}
                placeholder="Select organizers"
                dropdownContainerClassName="dropdown-append"
              />
            </div>
          </div>
        )}

        {/* Region/Province Section */}
        <div className="w-full">
          <label className="block text-sm text-gray-600 mb-1">Province</label>
          <div id="monthly-report-province-dropdown-container" className="relative">
            <MultiSelectDropdownInline
              testId="monthly-report-province-dropdown"
              options={provinces}
              onSelect={(selectedIds: string[]) => {
                setActiveFilters(prev => ({
                  ...prev,
                  province: selectedIds
                }));
              }}
              getValue={(option: { id: string; name: string }) => option.id}
              getDisplayText={(option: { id: string; name: string }) => option.name}
              selectedValues={activeFilters.province}
              placeholder="Select provinces"
              dropdownContainerClassName="dropdown-append"
            />
          </div>
        </div>

        {/* Union Status Section */}
        <div className="w-full">
          <label className="block text-sm text-gray-600 mb-1">Union Status</label>
          <div id="monthly-union-status-dropdown-container" className="relative">
            <MultiSelectDropdownInline
              testId="monthly-union-status-dropdown"
              options={unionStatusOptions}
              onSelect={(selectedIds: string[]) => {
                setActiveFilters(prev => ({
                  ...prev,
                  unionStatus: selectedIds
                }));
              }}
              getValue={(option: { id: string; name: string }) => option.id}
              getDisplayText={(option: { id: string; name: string }) => option.name}
              selectedValues={activeFilters.unionStatus || []}
              placeholder="Select union status"
              dropdownContainerClassName="dropdown-append"
            />
          </div>
        </div>

        {/* Local Unions Section */}
        <div className="w-full">
          <label className="block text-sm text-gray-600 mb-1">Local Union</label>
          <div id="local-unions-dropdown-container" className="relative">
            <MultiSelectDropdownInline
              testId="local-unions-dropdown"
              options={localUnions}
              onSelect={(selectedIds: string[]) => {
                setActiveFilters(prev => ({
                  ...prev,
                  localUnion: selectedIds
                }));
              }}
              getValue={(option: { id: string; name: string }) => option.id}
              getDisplayText={(option: { id: string; name: string }) => option.name}
              selectedValues={activeFilters.localUnion || []}
              placeholder="Select local unions"
              dropdownContainerClassName="dropdown-append"
            />
          </div>
        </div>


        {/* Apply Button */}
        <div className="w-full sm:col-span-2 lg:col-span-3 xl:col-span-5 flex justify-end mt-4 gap-2">
          <button
            id="monthly-report-apply-filters-button"
            className={`btn-primary whitespace-nowrap flex-1 sm:flex-none ${!isMonthlyOrganizerActivityFiltersValid() ? 'opacity-50 cursor-not-allowed' : ''}`}
            onClick={handleApplyFilters}
            disabled={!isMonthlyOrganizerActivityFiltersValid()}
          >
            Apply Filters
          </button>
          <button
            id="monthly-report-refresh-button"
            className="btn-secondary whitespace-nowrap"
            onClick={() => {
              setLoading(true);
              handleFilterClearAll();
            }}
          >
            <RefreshCcw className="h-4 w-4" />
          </button>
        </div>
      </div>
        </div>
      )}
    </div>
  );
};

export default MonthlyOrganizerActivityFilters;
